import { collection, getDocs, updateDoc, doc, query, where, addDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createDummyUserData } from './dummyData';

// 모든 사용자에게 sentMessages 필드 추가
export const initializeSentMessagesField = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    let updatedCount = 0;
    
    for (const userDoc of querySnapshot.docs) {
      const userData = userDoc.data();
      
      // sentMessages 필드가 없으면 추가
      if (!userData.sentMessages) {
        await updateDoc(doc(db, 'users', userDoc.id), {
          sentMessages: []
        });
        updatedCount++;
      }
    }
    
    return updatedCount;
  } catch (error) {
    console.error('sentMessages 필드 초기화 오류:', error);
    throw error;
  }
};

// 특정 사용자에게 sentMessages 필드 추가 (새로운 시간 기반 구조)
export const addSentMessagesFieldToUser = async (nickname: string, avatarNumber: string) => {
  try {
    const userInfo = await getUserFromTimeBasedCollection(nickname, avatarNumber);
    
    if (userInfo) {
      const { collectionName, docId } = userInfo;
      await updateDoc(doc(db, collectionName, docId), {
        sentMessages: []
      });
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('sentMessages 필드 추가 오류:', error);
    return false;
  }
};

// 시간 기반 컬렉션 이름 생성
export const getTimeBasedCollectionName = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  
  return `users_${year}${month}${day}_${hour}`;
};

// 현재 시간 기준 컬렉션 이름 가져오기
export const getCurrentCollectionName = (): string => {
  return getTimeBasedCollectionName(new Date());
};

// 컬렉션에 더미 유저 2명 자동 추가
export const addDummyUsersToCollection = async (collectionName: string): Promise<void> => {
  try {
    // 더미 유저 2명 생성
    const dummyUser1 = createDummyUserData();
    const dummyUser2 = createDummyUserData();
    
    // 첫 번째 더미 유저 추가
    await addDoc(collection(db, collectionName), {
      ...dummyUser1,
      createdAt: new Date(),
      collectionHour: new Date().getHours(),
      isDummyUser: true // 더미 유저임을 표시
    });
    
    // 두 번째 더미 유저 추가
    await addDoc(collection(db, collectionName), {
      ...dummyUser2,
      createdAt: new Date(),
      collectionHour: new Date().getHours(),
      isDummyUser: true // 더미 유저임을 표시
    });
  } catch (error) {
    console.error('더미 유저 추가 실패:', error);
    throw error;
  }
};

// 특정 시간대의 유저 컬렉션에 유저 추가
export const addUserToTimeBasedCollection = async (userData: any): Promise<string> => {
  try {
    const collectionName = getCurrentCollectionName();
    
    // 컬렉션이 비어있는지 확인하고, 비어있다면 더미 유저 2명 추가
    const usersRef = collection(db, collectionName);
    const querySnapshot = await getDocs(usersRef);
    
    if (querySnapshot.empty) {
      await addDummyUsersToCollection(collectionName);
    }
    
    const { addDoc } = await import('firebase/firestore');
    const docRef = await addDoc(collection(db, collectionName), {
      ...userData,
      createdAt: new Date(),
      collectionHour: new Date().getHours(), // 추가 정보
      isDummyUser: false // 실제 유저임을 표시
    });
    
    return docRef.id;
  } catch (error) {
    console.error('시간 기반 컬렉션에 유저 추가 실패:', error);
    throw error;
  }
};

// 특정 시간대의 유저 검색
export const findUserInTimeBasedCollection = async (
  nickname: string, 
  avatarNumber: string, 
  targetDate?: Date
): Promise<any> => {
  try {
    const searchDate = targetDate || new Date();
    const collectionName = getTimeBasedCollectionName(searchDate);
    
    const usersRef = collection(db, collectionName);
    const q = query(
      usersRef,
      where('nickname', '==', nickname),
      where('avatarNumber', '==', avatarNumber)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userData = querySnapshot.docs[0].data();
      return userData;
    }
    
    return null;
  } catch (error) {
    console.error('시간 기반 컬렉션에서 유저 검색 실패:', error);
    return null;
  }
};

// 유효한 유저 검색 (2시간 이내 생성된 유저)
export const findValidUser = async (nickname: string, avatarNumber: string): Promise<any> => {
  try {
    const now = new Date();
    const twoHoursAgo = new Date(now.getTime() - (2 * 60 * 60 * 1000));
    
    // 현재 시간부터 2시간 전까지의 컬렉션들을 검색
    for (let i = 0; i < 3; i++) {
      const searchDate = new Date(now.getTime() - (i * 60 * 60 * 1000));
      const collectionName = getTimeBasedCollectionName(searchDate);
      
      const userData = await findUserInTimeBasedCollection(nickname, avatarNumber, searchDate);
      if (userData) {
        const createdAt = userData.createdAt.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
        
        if (createdAt >= twoHoursAgo) {
          return userData;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('유효한 유저 검색 실패:', error);
    return null;
  }
};

// 시간 기반 컬렉션에서 사용자 정보 업데이트
export const updateUserInTimeBasedCollection = async (
  nickname: string,
  avatarNumber: string,
  updateData: any
): Promise<boolean> => {
  try {
    // 현재 시간부터 2시간 전까지의 컬렉션들을 검색하여 사용자 찾기
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const searchDate = new Date(now.getTime() - (i * 60 * 60 * 1000));
      const collectionName = getTimeBasedCollectionName(searchDate);
      
      const usersRef = collection(db, collectionName);
      const q = query(
        usersRef,
        where('nickname', '==', nickname),
        where('avatarNumber', '==', avatarNumber)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userRef = doc(db, collectionName, userDoc.id);
        
        await updateDoc(userRef, updateData);
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('사용자 정보 업데이트 중 오류 발생:', error);
    return false;
  }
};

// 시간 기반 컬렉션에서 사용자 정보 가져오기 (업데이트용)
export const getUserFromTimeBasedCollection = async (
  nickname: string,
  avatarNumber: string
): Promise<{ userData: any; collectionName: string; docId: string } | null> => {
  try {
    // 현재 시간부터 2시간 전까지의 컬렉션들을 검색
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const searchDate = new Date(now.getTime() - (i * 60 * 60 * 1000));
      const collectionName = getTimeBasedCollectionName(searchDate);
      
      const usersRef = collection(db, collectionName);
      const q = query(
        usersRef,
        where('nickname', '==', nickname),
        where('avatarNumber', '==', avatarNumber)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        
        return {
          userData,
          collectionName,
          docId: userDoc.id
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('getUserFromTimeBasedCollection: 오류 발생:', error);
    return null;
  }
};

// 2시간 이내 생성된 모든 사용자 가져오기
export const getAllUsersInTimeWindow = async (hours: number = 2): Promise<any[]> => {
  try {
    const allUsers: any[] = [];
    const now = new Date();
    
    // 지정된 시간 범위 내의 모든 컬렉션을 검색
    for (let i = 0; i <= hours; i++) {
      const searchDate = new Date(now.getTime() - (i * 60 * 60 * 1000));
      const collectionName = getTimeBasedCollectionName(searchDate);
      
      try {
        const usersRef = collection(db, collectionName);
        const querySnapshot = await getDocs(usersRef);
        
        querySnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          allUsers.push({
            id: `${collectionName}_${userDoc.id}`, // collectionName_docId 형태로 ID 생성
            ...userData,
            collectionName
          });
        });
      } catch (error) {
        // 컬렉션이 존재하지 않는 경우 무시하고 계속 진행
        continue;
      }
    }
    
    return allUsers;
  } catch (error) {
    console.error('사용자 목록 가져오기 중 오류 발생:', error);
    return [];
  }
};

// CollectionSentMessages 문서에 메시지 저장 (중복 방지)
export const saveMessageToCollectionSentMessages = async (
  collectionName: string,
  message: string,
  receiverNickname: string,
  senderNickname: string,
  senderAvatarNumber: string
): Promise<boolean> => {
  try {
    // CollectionSentMessages 문서 참조
    const collectionSentMessagesRef = doc(db, collectionName, 'CollectionSentMessages');
    
    // 문서가 존재하는지 확인
    const docSnap = await getDoc(collectionSentMessagesRef);
    
    // 메시지 고유 ID 생성 (중복 방지용)
    const messageId = `${senderNickname}_${receiverNickname}_${Date.now()}_${message.substring(0, 10)}`;
    
    const newSentMessage = {
      id: messageId, // 고유 ID 추가
      message: message,
      receiverNickname: receiverNickname,
      senderNickname: senderNickname,
      senderAvatarNumber: senderAvatarNumber,
      sentAt: new Date()
    };
    
    if (docSnap.exists()) {
      // 문서가 존재하면 sentMessages 배열에 추가
      const existingData = docSnap.data();
      const existingMessages = existingData.sentMessages || [];
      
      // 중복 메시지 확인 (같은 ID가 있는지 체크)
      const isDuplicate = existingMessages.some((msg: any) => msg.id === messageId);
      
      if (isDuplicate) {
        return true; // 중복이지만 성공으로 처리
      }
      
      const updatedMessages = [...existingMessages, newSentMessage];
      
      await updateDoc(collectionSentMessagesRef, {
        sentMessages: updatedMessages
      });
    } else {
      // 문서가 존재하지 않으면 새로 생성
      await setDoc(collectionSentMessagesRef, {
        sentMessages: [newSentMessage],
        createdAt: new Date(),
        collectionName: collectionName
      });
    }
    
    return true;
  } catch (error) {
    console.error('CollectionSentMessages에 메시지 저장 오류:', error);
    return false;
  }
};

// 메시지 수신을 위한 사용자 검색 (24시간 범위)
export const findUserForMessageReceiver = async (
  nickname: string,
  avatarNumber: string
): Promise<{ userData: any; collectionName: string; docId: string } | null> => {
  try {
    // 24시간 범위로 검색 (메시지 수신을 위해 더 넓은 범위)
    const now = new Date();
    
    for (let i = 0; i < 24; i++) { // 24시간 범위
      const searchDate = new Date(now.getTime() - (i * 60 * 60 * 1000));
      const collectionName = getTimeBasedCollectionName(searchDate);
      
      const usersRef = collection(db, collectionName);
      const q = query(
        usersRef,
        where('nickname', '==', nickname),
        where('avatarNumber', '==', avatarNumber)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        
        return {
          userData,
          collectionName,
          docId: userDoc.id
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('메시지 수신용 사용자 검색 중 오류 발생:', error);
    return null;
  }
};

// 오래된 컬렉션 정리 (2시간 이상 지난 컬렉션)
export const cleanupOldCollections = async (): Promise<void> => {
  try {
    // 현재 시간 기준으로 2시간 이상 지난 컬렉션들을 찾아서 삭제
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - (2 * 60 * 60 * 1000));
    
    // 이 부분은 실제 구현 시 Firestore의 컬렉션 목록 조회 API를 사용해야 합니다
    // 현재는 수동으로 정리하는 방식을 제안합니다
  } catch (error) {
    console.error('오래된 컬렉션 정리 실패:', error);
  }
};

